import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import * as dates from '@simplybenefits/sb-dates';
import { Text, Button, ContentBlock, Theme, Files, Loader, ContainerControlSite, Content, Modal, Icons } from '@simplybenefits/sb-ui-library';
import RenewalNotes from './RenewalNotes.js';
import { listFiles, createFolder, downloadFile, removeFile, uploadFile } from '../../../actions/files.js';
import AccountInformation from '../components/AccountInformation.js';
import { getRenewals, getAccount, completeRenewal } from '../../../actions/accounts.js';
import { getPdf } from '../../../actions/master.js';
import Header from '../../../components/Header.js';
import RenewalForm from './RenewalForm.js';
import Icon from '@mui/material/Icon';

/**
 * Renewal component responsible for rendering and managing the renewal process for a specific account.
 * @param currentAccountMeta 
 * @param classes
 * @param designs
 * @param plan
 * @param commissions
 * @param dispatch
 * @param loader
 * @param match
 */
const Renewal = ({ currentAccountMeta, classes, designs, plan, commissions, dispatch, loader, match }) => {
    const [ renewal, setRenewal ] = useState(null);
    const [ showRenewalForm, setShowRenewalForm ] = useState(false);
    const [ showCompleteModal, setShowCompleteModal ] = useState(false);

    /**
     * Fetches the selected renewal based on the `renewalID` from the URL parameters.
     */
    const _fetchRenewalData = async () => {
        if (!currentAccountMeta) {
            await dispatch(getAccount(match.params.accountID));
        }

        const fetchedRenewals = await dispatch(getRenewals(match.params.accountID));
        const selectedRenewal = fetchedRenewals.find((item) => item.renewalID === match.params.renewalID);
    
        setRenewal(selectedRenewal);
    };

    useEffect(() => {
        _fetchRenewalData();
    }, []);

    /**
     * Toggles the visibility of the renewal form.
     */
    const _toggleRenewalForm = () => {
        setShowRenewalForm(!showRenewalForm);
    };

    /**
     * Submits the renewal form
     * @param {Object} data
     */
    const _submitRenewalForm = async (data) => {
        const file = `Renewal Report-${ currentAccountMeta.groupNum }`;
        const downloadParams = { accountID: currentAccountMeta.accountID, renewalID: renewal.renewalID };
        const formData = { renewalID: renewal.renewalID, ...data };

        return await dispatch(getPdf('RenewalReport', file, downloadParams, formData));
    };

    /**
     * Completes the renewal process 
     */
    const _completeRenewal = async () => {
        const success = await dispatch(completeRenewal(currentAccountMeta.accountID, renewal.renewalID));
        
        if (success) {
            _fetchRenewalData();
            setShowCompleteModal(false);
        }
    };

    /**
     * Builds the breadcrumb navigation for the renewal page.
     * @returns {Array}
     */
    const _buildBreadcrumbs = () => [
        {
            title: 'Accounts',
            link: `/accounts`
        },
        {
            title: currentAccountMeta.company,
            link: `/accounts/${ currentAccountMeta.accountID }`
        },
        {
            title: 'Renewals',
            link: `/accounts/${ currentAccountMeta.accountID }/renewals`
        }
    ];

    /**
     * Determines the color for the renewal status based on its value.
     * @param {string} status
     * @returns {string} 
     */
    const _setStatusColor = (status) => {
        const { brandGreen, red, skyBlue, darkGrey } = Theme.colors;

        switch (status) {
            case 'Completed':
                return brandGreen;
            case 'Cancelled':
                return red;
            case 'In Renewal':
                return skyBlue;
            case 'Pending':
                return darkGrey;
            default:
                return darkGrey;
        }
    };

    const styles = {
        labelGrouping: {
            display: 'flex',
            flexDirection: 'column'
        }
    };

    if (!renewal) {
        return (
            <Loader/>
        );
    }

    if (showRenewalForm) {
        return (
            <RenewalForm
                renewal={ renewal } 
                accountMeta={ currentAccountMeta } 
                designs={ designs }
                classes={ classes }
                commissions={ commissions }
                plan={ plan }
                close={ () => _toggleRenewalForm } 
                onSubmit={ (...params) => _submitRenewalForm(...params) }
            />
        );
    }

    return (
        <ContainerControlSite>
            <Header
                subHeader={ `Renewal - ${ dates.formatDateForDisplay(renewal.renewalDate, 'll') }` }
                breadcrumbs={ _buildBreadcrumbs() }
                selectedTitle="accounts"
            />
            <Content>
                <AccountInformation accountMeta={ currentAccountMeta } customStyle={{ marginBottom: 25 }}/>

                {
                    renewal.status === 'In Renewal' ?
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
                            <Button
                                onClick={() => setShowCompleteModal(true)}
                                transparent
                            >
                                <Icon style={{ alignSelf: 'center', color: Theme.colors.darkBlue, marginRight: 10 }}>
                                    {Icons['Check Circle']}
                                </Icon>
                                Complete
                            </Button>

                            {
                                currentAccountMeta.planType.toLowerCase() === 'standard' && !currentAccountMeta.isAso && !currentAccountMeta.asoType ?
                                    <Button
                                        onClick={ () => _toggleRenewalForm() }
                                        transparent
                                        customStyle={{ marginLeft: '5px' }}
                                    >
                                        <Icon style={{ alignSelf: 'center', color: Theme.colors.darkBlue, marginRight: 10 }}>
                                            {Icons.Generate}
                                        </Icon>
                                        Generate Renewal Report
                                    </Button>
                                    : null
                            }
                        </div>
                        : null
                }

                <ContentBlock>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 30 }}>
                        <Text large bold>
                            Renewal - { dates.formatDateForDisplay(renewal.renewalDate, 'll')}
                        </Text>
                        <Text bold mediumLarge color={ _setStatusColor(renewal.status)}>
                            { renewal.status}
                        </Text>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
                        <div style={{ ...styles.labelGrouping, gridColumn: 'span 2' }}>
                            <Text bold medium>
                                Renewal ID
                            </Text>
                            <Text medium>
                                { renewal.uid }
                            </Text>
                        </div>
                    </div>
    
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 30 }}>
                        <div style={ styles.labelGrouping }>
                            <Text bold medium>
                                Renewal Date
                            </Text>
                            <Text medium>
                                { dates.formatDateForDisplay(renewal.renewalDate, 'll') } 
                            </Text>
                        </div>
    
                        <div style={ styles.labelGrouping }>
                            <Text bold medium>
                                Experience Period Start
                            </Text>
                            <Text medium>
                                { dates.formatDateForDisplay(renewal.periodStart, 'll') } 
                            </Text>
                        </div>
    
                        <div style={ styles.labelGrouping }>
                            <Text bold medium>
                                Experience Period End
                            </Text>
                            <Text medium>
                                { dates.formatDateForDisplay(renewal.periodEnd, 'll') } 
                            </Text>
                        </div>
    
                        <div style={ styles.labelGrouping }>
                            <Text bold medium>
                                Renewal Pickup Date
                            </Text>
                            <Text medium>
                                { dates.formatDateForDisplay(renewal.renewalPickupDate, 'll') } 
                            </Text>
                        </div>
    
                        <div style={ styles.labelGrouping }>
                            <Text bold medium>
                                Date Created
                            </Text>
                            <Text medium>
                                { dates.formatDateForDisplay(renewal.dateAdded, "ll") }  
                            </Text>
                        </div>

                        {
                            renewal.completedByName ?
                                <div style={ styles.labelGrouping }>
                                    <Text bold medium>
                                        Renewal Completed By
                                    </Text>
                                    <Text medium>
                                        { renewal.completedByName }
                                    </Text>
                                </div>
                                : null
                        }
                    </div>
    
                    <Files
                        isMasterSite={ true }
                        type="accounts"
                        id={ currentAccountMeta?.accountID }
                        parentFolder={`renewals/${ renewal.uid }`}
                        dispatch={ dispatch }
                        listFiles={ listFiles }
                        createFolder={ createFolder }
                        uploadFile={ uploadFile }
                        downloadFile={ downloadFile }
                        removeFile={ removeFile }
                        loader={ loader }
                        noContainer={ true }
                        isLocked={ renewal.status !== "In Renewal"}
                        style={{ marginBottom: 30 }}
                    />

                    <RenewalNotes
                        accountID={ currentAccountMeta.accountID }
                        renewalID={ renewal.renewalID }
                        renewalDate={renewal?.renewalDate}
                    />
                </ContentBlock>
            </Content>

            <Modal
                open={Boolean(showCompleteModal)}
                onClose={ () => setShowCompleteModal(false) }
                title="Complete Renewal"
                loader={ loader }
                primaryButton={{
                    label: "Complete Renewal",
                    onClick: () => _completeRenewal()
                }}
            >
                <Text medium>
                    Are you sure you want to mark this Renewal as Completed? This action cannot be undone, and all related files will be locked—allowing only viewing and downloading, with no option to add or remove files.
                </Text>
            </Modal>
        </ContainerControlSite>
    );
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    currentAccountMeta: state.accounts.currentAccount.meta,
    classes: state.accounts.currentAccount.classes,
    designs: state.accounts.currentAccount.designs,
    plan: state.accounts.currentAccount.plan,
    commissions: state.accounts.currentAccount.commissions
});

export default (connect(mapStateToProps)(Renewal));